import React, { useState, useEffect } from 'react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { fetchAllContent, updateSection, getPickups, updatePickupStatus, deletePickup } from '@/data/api';
import { Loader2, Save, Trash2, Check, Clock, Package, ArrowLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { PricingEditor } from '@/components/admin/PricingEditor';

const AdminPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [content, setContent] = useState(null);
  const [pickups, setPickups] = useState([]);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('hero');

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [contentData, pickupsData] = await Promise.all([
        fetchAllContent(),
        getPickups()
      ]);
      setContent(contentData);
      setPickups(pickupsData);
    } catch (e) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (section) => {
    setSaving(true);
    try {
      await updateSection(section, content[section]);
      toast.success(`${section} section updated successfully!`);
    } catch (e) {
      toast.error('Failed to update section');
    } finally {
      setSaving(false);
    }
  };

  const updateContent = (section, key, value) => {
    setContent(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        [key]: value
      }
    }));
  };

  const updateArrayContent = (section, index, key, value) => {
    setContent(prev => {
      const newArray = [...prev[section]];
      newArray[index] = { ...newArray[index], [key]: value };
      return { ...prev, [section]: newArray };
    });
  };

  const handleStatusUpdate = async (orderId, newStatus) => {
    try {
      await updatePickupStatus(orderId, newStatus);
      setPickups(prev => prev.map(p => p.id === orderId ? { ...p, status: newStatus } : p));
      toast.success('Order status updated');
    } catch (e) {
      toast.error('Failed to update status');
    }
  };

  const handleDeleteOrder = async (orderId) => {
    if (!window.confirm('Delete this order?')) return;
    try {
      await deletePickup(orderId);
      setPickups(prev => prev.filter(p => p.id !== orderId));
      toast.success('Order deleted');
    } catch (e) {
      toast.error('Failed to delete order');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button variant="ghost" size="sm" onClick={() => navigate('/')}>
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to Website
              </Button>
              <Separator orientation="vertical" className="h-6" />
              <h1 className="text-2xl font-bold text-gray-900">Blitzwash Admin</h1>
            </div>
            <Badge variant="secondary">Content Management System</Badge>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 py-8">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="mb-6">
            <TabsTrigger value="hero">Hero</TabsTrigger>
            <TabsTrigger value="services">Services</TabsTrigger>
            <TabsTrigger value="stores">Stores</TabsTrigger>
            <TabsTrigger value="pricing">Pricing</TabsTrigger>
            <TabsTrigger value="testimonials">Testimonials</TabsTrigger>
            <TabsTrigger value="faqs">FAQs</TabsTrigger>
            <TabsTrigger value="contact">Contact</TabsTrigger>
            <TabsTrigger value="orders">Pickup Orders</TabsTrigger>
          </TabsList>

          {/* Hero Section */}
          <TabsContent value="hero">
            <Card>
              <CardHeader>
                <CardTitle>Hero Section</CardTitle>
                <CardDescription>Edit the main hero section content</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Headline</label>
                  <Textarea
                    value={content?.hero?.headline || ''}
                    onChange={(e) => updateContent('hero', 'headline', e.target.value)}
                    rows={2}
                    data-testid="hero-headline-input"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Subheadline</label>
                  <Textarea
                    value={content?.hero?.subheadline || ''}
                    onChange={(e) => updateContent('hero', 'subheadline', e.target.value)}
                    rows={3}
                    data-testid="hero-subheadline-input"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Badge Text</label>
                  <Input
                    value={content?.hero?.badgeText || ''}
                    onChange={(e) => updateContent('hero', 'badgeText', e.target.value)}
                    data-testid="hero-badge-input"
                  />
                </div>
                <Button onClick={() => handleSave('hero')} disabled={saving} data-testid="save-hero-btn">
                  {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                  Save Changes
                </Button>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Services Section */}
          <TabsContent value="services">
            <div className="space-y-4">
              {content?.services?.map((service, idx) => (
                <Card key={idx}>
                  <CardHeader>
                    <CardTitle>Service {idx + 1}: {service.title}</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium mb-2">Title</label>
                      <Input
                        value={service.title}
                        onChange={(e) => updateArrayContent('services', idx, 'title', e.target.value)}
                        data-testid={`service-${idx}-title-input`}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-2">Description</label>
                      <Textarea
                        value={service.description}
                        onChange={(e) => updateArrayContent('services', idx, 'description', e.target.value)}
                        rows={3}
                        data-testid={`service-${idx}-description-input`}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-2">Price Label</label>
                      <Input
                        value={service.priceLabel}
                        onChange={(e) => updateArrayContent('services', idx, 'priceLabel', e.target.value)}
                        data-testid={`service-${idx}-price-input`}
                      />
                    </div>
                  </CardContent>
                </Card>
              ))}
              <Button onClick={() => handleSave('services')} disabled={saving} data-testid="save-services-btn">
                {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                Save All Services
              </Button>
            </div>
          </TabsContent>

          {/* Stores Section */}
          <TabsContent value="stores">
            <div className="space-y-4">
              <Card>
                <CardHeader>
                  <CardTitle>Stores</CardTitle>
                  <CardDescription>Edit phone, WhatsApp, map link and status for each store. WhatsApp must start with https://wa.me/</CardDescription>
                </CardHeader>
              </Card>
              {content?.stores?.map((store, idx) => (
                <Card key={idx}>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      {store.name}
                      <Badge variant={store.status === 'open' ? 'default' : 'secondary'}>
                        {store.status === 'open' ? 'Open' : 'Coming Soon'}
                      </Badge>
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium mb-2">Store Name</label>
                        <Input
                          value={store.name || ''}
                          onChange={(e) => updateArrayContent('stores', idx, 'name', e.target.value)}
                          data-testid={`store-${idx}-name-input`}
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium mb-2">Area / Address</label>
                        <Input
                          value={store.area || ''}
                          onChange={(e) => updateArrayContent('stores', idx, 'area', e.target.value)}
                          data-testid={`store-${idx}-area-input`}
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium mb-2">Status</label>
                        <select
                          value={store.status || 'coming-soon'}
                          onChange={(e) => updateArrayContent('stores', idx, 'status', e.target.value)}
                          className="w-full h-10 rounded-md border border-input bg-background px-3 text-sm"
                          data-testid={`store-${idx}-status-input`}
                        >
                          <option value="open">Open</option>
                          <option value="coming-soon">Coming Soon</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium mb-2">Phone Number</label>
                        <Input
                          value={store.phone || ''}
                          placeholder="+91 XXXXX XXXXX"
                          onChange={(e) => updateArrayContent('stores', idx, 'phone', e.target.value)}
                          data-testid={`store-${idx}-phone-input`}
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium mb-2">WhatsApp URL</label>
                        <Input
                          value={store.whatsapp || ''}
                          placeholder="https://wa.me/91XXXXXXXXXX"
                          onChange={(e) => updateArrayContent('stores', idx, 'whatsapp', e.target.value)}
                          data-testid={`store-${idx}-whatsapp-input`}
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium mb-2">Google Maps Link</label>
                        <Input
                          value={store.mapLink || ''}
                          placeholder="https://maps.app.goo.gl/..."
                          onChange={(e) => updateArrayContent('stores', idx, 'mapLink', e.target.value)}
                          data-testid={`store-${idx}-maplink-input`}
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium mb-2">Latitude (for nearest-store detection)</label>
                        <Input
                          type="number"
                          step="0.0001"
                          value={store.lat ?? ''}
                          placeholder="26.9051"
                          onChange={(e) => updateArrayContent('stores', idx, 'lat', e.target.value === '' ? undefined : parseFloat(e.target.value))}
                          data-testid={`store-${idx}-lat-input`}
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium mb-2">Longitude</label>
                        <Input
                          type="number"
                          step="0.0001"
                          value={store.lng ?? ''}
                          placeholder="75.7245"
                          onChange={(e) => updateArrayContent('stores', idx, 'lng', e.target.value === '' ? undefined : parseFloat(e.target.value))}
                          data-testid={`store-${idx}-lng-input`}
                        />
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
              <Button onClick={() => handleSave('stores')} disabled={saving} data-testid="save-stores-btn">
                {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                Save All Stores
              </Button>
            </div>
          </TabsContent>

          {/* Pricing Section */}
          <TabsContent value="pricing">
            <PricingEditor
              pricing={content?.pricing || {}}
              onChange={(newPricing) => setContent((prev) => ({ ...prev, pricing: newPricing }))}
              onSave={() => handleSave('pricing')}
              saving={saving}
            />
          </TabsContent>

          {/* Testimonials Section */}
          <TabsContent value="testimonials">
            <div className="space-y-4">
              {content?.testimonials?.map((testimonial, idx) => (
                <Card key={idx}>
                  <CardHeader>
                    <CardTitle>{testimonial.name}</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium mb-2">Name</label>
                        <Input
                          value={testimonial.name}
                          onChange={(e) => updateArrayContent('testimonials', idx, 'name', e.target.value)}
                          data-testid={`testimonial-${idx}-name-input`}
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium mb-2">Location</label>
                        <Input
                          value={testimonial.location}
                          onChange={(e) => updateArrayContent('testimonials', idx, 'location', e.target.value)}
                          data-testid={`testimonial-${idx}-location-input`}
                        />
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-2">Review Text</label>
                      <Textarea
                        value={testimonial.text}
                        onChange={(e) => updateArrayContent('testimonials', idx, 'text', e.target.value)}
                        rows={3}
                        data-testid={`testimonial-${idx}-text-input`}
                      />
                    </div>
                  </CardContent>
                </Card>
              ))}
              <Button onClick={() => handleSave('testimonials')} disabled={saving} data-testid="save-testimonials-btn">
                {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                Save All Testimonials
              </Button>
            </div>
          </TabsContent>

          {/* FAQs Section */}
          <TabsContent value="faqs">
            <div className="space-y-4">
              {content?.faqs?.map((faq, idx) => (
                <Card key={idx}>
                  <CardContent className="pt-6 space-y-4">
                    <div>
                      <label className="block text-sm font-medium mb-2">Question</label>
                      <Input
                        value={faq.question}
                        onChange={(e) => updateArrayContent('faqs', idx, 'question', e.target.value)}
                        data-testid={`faq-${idx}-question-input`}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-2">Answer</label>
                      <Textarea
                        value={faq.answer}
                        onChange={(e) => updateArrayContent('faqs', idx, 'answer', e.target.value)}
                        rows={3}
                        data-testid={`faq-${idx}-answer-input`}
                      />
                    </div>
                  </CardContent>
                </Card>
              ))}
              <Button onClick={() => handleSave('faqs')} disabled={saving} data-testid="save-faqs-btn">
                {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                Save All FAQs
              </Button>
            </div>
          </TabsContent>

          {/* Contact Section */}
          <TabsContent value="contact">
            <Card>
              <CardHeader>
                <CardTitle>Contact Information</CardTitle>
                <CardDescription>Update contact details</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-2">Phone</label>
                    <Input
                      value={content?.contact?.phone || ''}
                      onChange={(e) => updateContent('contact', 'phone', e.target.value)}
                      data-testid="contact-phone-input"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">Email</label>
                    <Input
                      value={content?.contact?.email || ''}
                      onChange={(e) => updateContent('contact', 'email', e.target.value)}
                      data-testid="contact-email-input"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">WhatsApp URL</label>
                    <Input
                      value={content?.contact?.whatsapp || ''}
                      onChange={(e) => updateContent('contact', 'whatsapp', e.target.value)}
                      data-testid="contact-whatsapp-input"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">Business Hours</label>
                    <Input
                      value={content?.contact?.hours || ''}
                      onChange={(e) => updateContent('contact', 'hours', e.target.value)}
                      data-testid="contact-hours-input"
                    />
                  </div>
                </div>
                <Button onClick={() => handleSave('contact')} disabled={saving} data-testid="save-contact-btn">
                  {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                  Save Changes
                </Button>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Pickup Orders */}
          <TabsContent value="orders">
            <Card>
              <CardHeader>
                <CardTitle>Pickup Orders</CardTitle>
                <CardDescription>Manage customer pickup requests</CardDescription>
              </CardHeader>
              <CardContent>
                {pickups.length === 0 ? (
                  <div className="text-center py-12 text-gray-500">
                    <Package className="w-12 h-12 mx-auto mb-4 opacity-50" />
                    <p>No pickup orders yet</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {pickups.map((order) => (
                      <Card key={order.id} className="border">
                        <CardContent className="pt-6">
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-2">
                                <h3 className="font-semibold text-lg" data-testid={`order-${order.id}-name`}>{order.name}</h3>
                                <Badge 
                                  variant={order.status === 'pending' ? 'secondary' : order.status === 'completed' ? 'default' : 'outline'}
                                  data-testid={`order-${order.id}-status`}
                                >
                                  {order.status}
                                </Badge>
                              </div>
                              <div className="grid grid-cols-2 gap-2 text-sm text-gray-600">
                                <div><span className="font-medium">Phone:</span> {order.phone}</div>
                                <div><span className="font-medium">Service:</span> {order.service}</div>
                                <div><span className="font-medium">Time:</span> {order.time_slot}</div>
                                <div><span className="font-medium">Date:</span> {new Date(order.created_at).toLocaleDateString()}</div>
                              </div>
                              {order.address && (
                                <div className="mt-2 text-sm text-gray-600">
                                  <span className="font-medium">Address:</span> {order.address}
                                </div>
                              )}
                            </div>
                            <div className="flex flex-col gap-2 ml-4">
                              {order.status === 'pending' && (
                                <Button 
                                  size="sm" 
                                  variant="outline"
                                  onClick={() => handleStatusUpdate(order.id, 'in-progress')}
                                  data-testid={`order-${order.id}-progress-btn`}
                                >
                                  <Clock className="w-4 h-4 mr-2" />
                                  In Progress
                                </Button>
                              )}
                              {order.status === 'in-progress' && (
                                <Button 
                                  size="sm"
                                  onClick={() => handleStatusUpdate(order.id, 'completed')}
                                  data-testid={`order-${order.id}-complete-btn`}
                                >
                                  <Check className="w-4 h-4 mr-2" />
                                  Complete
                                </Button>
                              )}
                              <Button 
                                size="sm" 
                                variant="destructive"
                                onClick={() => handleDeleteOrder(order.id)}
                                data-testid={`order-${order.id}-delete-btn`}
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default AdminPage;